/**
 * GLOBAL HOVER EVENT BUS (no React state)
 *
 * We use a single shared EventTarget ("bus") to broadcast which widget is
 * currently hovered. Widgets call `emitHover(id)` on pointer enter/leave.
 * Other widgets subscribe via `onHover(cb)` and react to those events.
 *
 * Why not React state?
 * - Using React state for hover would re-render many components unnecessarily
 * - This avoids prop drilling, context, and global state tools
 * - Widgets only toggle a CSS class based on events — zero re-renders
 *
 * In short: a tiny pub/sub system to sync hover highlighting between widgets
 * without impacting React rendering.
 */

// Payload shape for hover events
// hoveredWidgetId = id of the widget currently hovered OR null when leaving
export type HoverPayload = { hoveredWidgetId: number | null };

// Create a tiny pub/sub using the browser's EventTarget
const BUS = new EventTarget();
const EVT = 'widget:hover';

// --- EMITTER ---------------------------------------------------------------
// Call this to broadcast "widget X is hovered" or "none is hovered"
export function emitHover(hoveredWidgetId: number | null) {
    // CustomEvent carries our payload in `detail`
    BUS.dispatchEvent(new CustomEvent<HoverPayload>(EVT, { detail: { hoveredWidgetId } }));
}

// --- SUBSCRIBER ------------------------------------------------------------
// Call this once in each widget that wants to react to global hover changes
// Returns a cleanup function so it can unsubscribe on unmount
export function onHover(cb: (payload: HoverPayload) => void) {
    const handler = (e: Event) => {
        // EventTarget gives plain Event, so cast to CustomEvent to get `.detail`
        cb((e as CustomEvent<HoverPayload>).detail);
    };
    BUS.addEventListener(EVT, handler);
    return () => BUS.removeEventListener(EVT, handler);
}
